// frontend/src/pages/properties/PropertyDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiTrash2, FiUpload, FiVideo, FiLink, FiX, FiMapPin, FiPlus, FiEdit, FiHome, FiImage, FiGrid, FiList, FiUser, FiTool } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { propertyAPI, propertyUnitAPI } from '../../services/api';

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ==================== UTILITAIRES ====================
const getFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000'}${url}`;
  }
  return url;
};

const getVideoEmbed = (url) => {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');
  const [ownerForm, setOwnerForm] = useState({ first_name: '', last_name: '', email: '', phone: '', ownership_percentage: '' });
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showVideoUrlModal, setShowVideoUrlModal] = useState(false);
  const [videoUrlForm, setVideoUrlForm] = useState({ url: '', title: '' });
  const [statuses, setStatuses] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitsView, setUnitsView] = useState('grid'); // 'grid' | 'table'
  const [unitForm, setUnitForm] = useState({
    unit_number: '', floor: '', unit_type: 'apartment', surface_area: '', num_rooms: '', num_bedrooms: '', num_bathrooms: '', is_furnished: false, monthly_rent: '', deposit_amount: '', agency_fee: '0', status: 'available', description: ''
  });

  const handleOpenUnitModal = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setUnitForm({
        unit_number: unit.unit_number || '',
        floor: unit.floor !== null && unit.floor !== undefined ? unit.floor : '',
        unit_type: unit.unit_type || 'apartment',
        surface_area: unit.surface_area || '',
        num_rooms: unit.num_rooms || '',
        num_bedrooms: unit.num_bedrooms || '',
        num_bathrooms: unit.num_bathrooms || '',
        is_furnished: !!unit.is_furnished,
        monthly_rent: unit.monthly_rent || '',
        deposit_amount: unit.deposit_amount || '',
        agency_fee: unit.agency_fee != null ? String(unit.agency_fee) : (unit.charges != null ? String(unit.charges) : '0'),
        status: unit.status || 'available',
        description: unit.description || ''
      });
    } else {
      setEditingUnit(null);
      setUnitForm({
        unit_number: '', floor: '', unit_type: 'apartment', surface_area: '', num_rooms: '', num_bedrooms: '', num_bathrooms: '', is_furnished: false, monthly_rent: '', deposit_amount: '', agency_fee: '0', status: 'available', description: ''
      });
    }
    setShowUnitModal(true);
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    try {
      if (editingUnit) {
        await propertyUnitAPI.update(editingUnit.id, unitForm);
      } else {
        await propertyUnitAPI.create(id, unitForm);
      }
      setShowUnitModal(false);
      await loadProperty();
    } catch (err) {
      console.error('Error saving unit:', err);
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde de l\'unité');
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Supprimer cette unité ?')) return;
    try {
      await propertyUnitAPI.delete(unitId);
      await loadProperty();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression de l\'unité');
    }
  };

  const getUnitStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <span className="badge" style={{ background: '#10b98120', color: '#10b981' }}>Disponible</span>;
      case 'occupied':
        return <span className="badge" style={{ background: '#ef444420', color: '#ef4444' }}>Occupé</span>;
      case 'reserved':
        return <span className="badge" style={{ background: '#f59e0b20', color: '#f59e0b' }}>Réservé</span>;
      case 'maintenance':
        return <span className="badge" style={{ background: '#3b82f620', color: '#3b82f6' }}>Maintenance</span>;
      case 'unavailable':
      default:
        return <span className="badge" style={{ background: '#6b728020', color: '#6b7280' }}>Indisponible</span>;
    }
  };

  // Charger les données du bien
  const loadProperty = async () => {
    try {
      const { data } = await propertyAPI.getById(id);
      setProperty(data);
      console.log('Property loaded:', data);
    } catch (error) {
      console.error('Error loading property:', error);
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async () => {
    try {
      const { data } = await propertyAPI.getStatuses();
      setStatuses(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadProperty();
    loadStatuses();
    
    // Vérifier les query params (ex: ?action=add_unit)
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'add_unit') {
      setTab('units');
      // Petit délai pour s'assurer que le composant est monté
      setTimeout(() => {
        handleOpenUnitModal();
        // Remove query param to avoid reopening on refresh
        navigate(`/properties/${id}`, { replace: true });
      }, 500);
    }
  }, [id, location.search, navigate]);

  const handleStatusChange = async (newStatusId) => {
    if (!newStatusId) return;
    try {
      setUpdatingStatus(true);
      await propertyAPI.update(id, { status_id: newStatusId });
      await loadProperty();
      alert('Statut mis à jour !');
    } catch (err) {
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Supprimer ce bien ?')) {
      await propertyAPI.delete(id);
      navigate('/properties');
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const formData = new FormData();
    for (let f of files) formData.append('images', f);
    try {
      await propertyAPI.uploadImages(id, formData);
      await loadProperty();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Erreur upload des images');
    }
  };

  const handleRemoveImage = async (imageId) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await propertyAPI.removeImage(id, imageId);
      await loadProperty();
    } catch (err) {
      alert('Erreur suppression image');
    }
  };

  // const handleVideoUpload = async (e) => {
  //   const files = e.target.files;
  //   if (!files.length) return;
  //   const formData = new FormData();
  //   for (let f of files) formData.append('videos', f);
  //   try {
  //     await propertyAPI.uploadVideos(id, formData);
  //     await loadProperty();
  //   } catch (err) {
  //     console.error('Video upload error:', err);
  //     alert('Erreur upload vidéo');
  //   }
  // };
const handleVideoUpload = async (e) => {
  const files = e.target.files;
  if (!files.length) return;
  const formData = new FormData();
  for (let f of files) {
    formData.append('videos', f);  // ← Doit correspondre à uploadVideo.array('videos')
  }
  try {
    await propertyAPI.uploadVideos(id, formData);
    await loadProperty();
  } catch (err) {
    console.error('Video upload error:', err.response?.data);  // ← Log l'erreur serveur
    alert('Erreur upload vidéo: ' + (err.response?.data?.error || 'inconnue'));
  }
};
  const handleAddVideoUrl = async (e) => {
    e.preventDefault();
    try {
      await propertyAPI.addVideoUrl(id, videoUrlForm);
      await loadProperty();
      setShowVideoUrlModal(false);
      setVideoUrlForm({ url: '', title: '' });
    } catch (err) {
      console.error('Add video URL error:', err);
      alert('Erreur ajout URL vidéo');
    }
  };

  const handleRemoveVideo = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    try {
      await propertyAPI.removeVideo(id, videoId);
      await loadProperty();
    } catch (err) {
      console.error('Remove video error:', err);
      alert('Erreur suppression vidéo');
    }
  };

  const handleAddOwner = async (e) => {
    e.preventDefault();
    try {
      await propertyAPI.addOwner(id, ownerForm);
      await loadProperty();
      setShowOwnerModal(false);
      setOwnerForm({ first_name: '', last_name: '', email: '', phone: '', ownership_percentage: '' });
    } catch (err) {
      console.error('Add owner error:', err);
      alert('Erreur ajout propriétaire');
    }
  };

  const handleRemoveOwner = async (ownerId) => {
    if (window.confirm('Supprimer ce propriétaire ?')) {
      try {
        await propertyAPI.removeOwner(id, ownerId);
        await loadProperty();
      } catch (err) {
        console.error('Remove owner error:', err);
        alert('Erreur suppression propriétaire');
      }
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!property) return null;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/properties')}>
            <FiArrowLeft /> Retour
          </button>
          <h1>{property.title}</h1>
          {property.status_name && (
            <span className="badge" style={{ background: property.status_color + '20', color: property.status_color }}>
              {property.status_name}
            </span>
          )}
          {property.is_multi_unit ? (
            <span className="badge" style={{ background: '#8b5cf620', color: '#8b5cf6' }}>
              Immeuble multi-unités ({property.occupied_units_count || 0}/{property.units_count || 0} occupés)
            </span>
          ) : (
            <span className="badge" style={{ background: '#0284c720', color: '#0284c7' }}>
              Maison / Unité individuelle
            </span>
          )}
          <button className={`btn btn-sm ${viewMode === 'map' ? 'btn-primary' : 'btn-secondary'}`} 
                  onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}>
            <FiMapPin /> {viewMode === 'list' ? 'Vue carte' : 'Vue liste'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {statuses && statuses.length > 0 && (
            <select 
              className="form-control" 
              style={{ width: 'auto' }}
              value={property.status_id || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
            >
              <option value="">Modifier le statut</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <button className="btn btn-danger btn-sm" onClick={handleDelete}>
            <FiTrash2 /> Supprimer
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="tabs">
        {['info', 'units', 'images', 'videos', 'documents', 'propriétaires'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'info' && 'Informations'}
            {t === 'units' && `Unités / Appartements (${property.units?.length || 0})`}
            {t === 'images' && `Images (${property.images?.length || 0})`}
            {t === 'videos' && `Vidéos (${property.videos?.length || 0})`}
            {t === 'documents' && 'Documents'}
            {t === 'propriétaires' && `Propriétaires (${property.owners?.length || 0})`}
          </button>
        ))}
      </div>

      {/* Onglet Informations */}
      {tab === 'info' && (
        <div className="detail-grid">
          <div>
            <div className="card">
              <div className="card-header"><h2>Localisation</h2></div>
              <div className="card-body">
                <div className="detail-row"><div className="detail-label">Adresse</div><div className="detail-value">{property.address || '-'}</div></div>
                <div className="detail-row"><div className="detail-label">Ville</div><div className="detail-value">{property.city || '-'}</div></div>
                <div className="detail-row"><div className="detail-label">Quartier</div><div className="detail-value">{property.neighborhood || '-'}</div></div>
                <div className="detail-row"><div className="detail-label">Type</div><div className="detail-value">{property.type_name || '-'}</div></div>
                <div className="detail-row"><div className="detail-label">Transaction</div><div className="detail-value">{property.transaction_type === 'rent' ? 'Location' : 'Vente'}</div></div>
                <div className="detail-row"><div className="detail-label">Prix</div><div className="detail-value">{parseInt(property.price || 0).toLocaleString()} XOF{property.transaction_type === 'rent' ? '/mois' : ''}</div></div>
                {property.surface_area && (
                  <div className="detail-row"><div className="detail-label">Surface</div><div className="detail-value">{property.surface_area} m²</div></div>
                )}
                {property.num_rooms && (
                  <div className="detail-row"><div className="detail-label">Pièces</div><div className="detail-value">{property.num_rooms}</div></div>
                )}
                {property.latitude && property.longitude && (
                  <div style={{ marginTop: 16, borderRadius: 8, overflow: 'hidden', height: 300 }}>
                    <MapContainer
                      center={[parseFloat(property.latitude), parseFloat(property.longitude)]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap contributors'
                      />
                      <Marker position={[parseFloat(property.latitude), parseFloat(property.longitude)]}>
                        <Popup>
                          <strong>{property.title}</strong><br />
                          {property.type_name && <span>{property.type_name} — </span>}
                          {property.address || property.city}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="card">
              <div className="card-header"><h2>Description</h2></div>
              <div className="card-body"><p>{property.description || 'Aucune description'}</p></div>
            </div>
            {property.features && property.features.length > 0 && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-header"><h2>Caractéristiques</h2></div>
                <div className="card-body">
                  {property.features.map(f => (
                    <span key={f.id} className="badge badge-info" style={{ margin: 4 }}>
                      {f.name} {f.value ? `- ${f.value}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Onglet Images */}
      {tab === 'images' && (
        <div className="card">
          <div className="card-header">
            <h2>Images ({property.images?.length || 0})</h2>
            <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
              <FiUpload /> Ajouter des images
              <input type="file" multiple accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {property.images?.map(img => (
                <div key={img.id} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
                  <img 
                    src={getFileUrl(img.url)} 
                    alt={img.alt_text} 
                    style={{ width: '100%', height: 150, objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = '/placeholder-image.jpg';
                      e.target.onerror = null;
                    }}
                  />
                  <button
                    onClick={() => handleRemoveImage(img.id)}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', color: 'white' }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                  {img.is_primary && (
                    <span style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                      Principale
                    </span>
                  )}
                </div>
              ))}
              {(!property.images || property.images.length === 0) && (
                <div className="empty-state" style={{ gridColumn: '1/-1' }}>
                  <p>Aucune image. Cliquez sur "Ajouter des images" pour commencer.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onglet Vidéos */}
      {tab === 'videos' && (
        <div className="card">
          <div className="card-header">
            <h2>Vidéos ({property.videos?.length || 0})</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                <FiUpload /> Uploader une vidéo
                <input type="file" multiple accept="video/*" onChange={handleVideoUpload} style={{ display: 'none' }} />
              </label>
              <button className="btn btn-primary btn-sm" onClick={() => setShowVideoUrlModal(true)}>
                <FiLink /> Ajouter une URL
              </button>
            </div>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
              {property.videos?.map(video => {
                const videoUrl = getFileUrl(video.url);
                const embedUrl = getVideoEmbed(videoUrl);
                return (
                  <div key={video.id} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        style={{ width: '100%', height: 200, border: 'none' }}
                        allowFullScreen
                        title={video.title || 'Vidéo'}
                      />
                    ) : (
                      <video
                        src={videoUrl}
                        controls
                        style={{ width: '100%', height: 200, objectFit: 'cover', background: '#000' }}
                        onError={(e) => {
                          console.error('Video error:', videoUrl);
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{video.title || 'Sans titre'}</span>
                      <button className="btn btn-danger btn-sm" onClick={() => handleRemoveVideo(video.id)}>
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {(!property.videos || property.videos.length === 0) && (
                <div className="empty-state" style={{ gridColumn: '1/-1' }}>
                  <FiVideo size={32} />
                  <p>Aucune vidéo. Ajoutez une vidéo par upload ou par URL.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onglet Documents */}
      {tab === 'documents' && (
        <div className="card">
          <div className="card-header"><h2>Documents</h2></div>
          <div className="card-body">
            {property.documents?.length > 0 ? (
              property.documents.map(doc => (
                <div key={doc.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <a href={getFileUrl(doc.file_url)} target="_blank" rel="noreferrer">{doc.name}</a>
                </div>
              ))
            ) : (
              <div className="empty-state"><p>Aucun document</p></div>
            )}
          </div>
        </div>
      )}

      {/* Onglet Propriétaires */}
      {tab === 'propriétaires' && (
        <div className="card">
          <div className="card-header">
            <h2>Propriétaires ({property.owners?.length || 0})</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowOwnerModal(true)}>
              <FiPlus /> Ajouter un propriétaire
            </button>
          </div>
          <div className="card-body">
            {property.owners?.length > 0 ? (
              property.owners.map(o => (
                <div key={o.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{o.first_name} {o.last_name}</strong>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {o.email && <span>{o.email} | </span>}
                      {o.phone && <span>{o.phone} | </span>}
                      {o.ownership_percentage && <span>{o.ownership_percentage}%</span>}
                    </div>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => handleRemoveOwner(o.id)}>
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="empty-state"><p>Aucun propriétaire enregistré</p></div>
            )}
          </div>
        </div>
      )}

      {/* Onglet Unités / Appartements */}
      {tab === 'units' && (() => {
        const units = property.units || [];
        const nbAvailable   = units.filter(u => (u.computed_status || u.status) === 'available').length;
        const nbOccupied    = units.filter(u => (u.computed_status || u.status) === 'occupied').length;
        const nbMaintenance = units.filter(u => (u.computed_status || u.status) === 'maintenance').length;
        const nbReserved    = units.filter(u => (u.computed_status || u.status) === 'reserved').length;
        const occupancyRate = units.length > 0 ? Math.round((nbOccupied / units.length) * 100) : 0;

        const unitTypeLabel = (t) => ({ apartment: 'Appt', studio: 'Studio', office: 'Bureau', shop: 'Commerce', other: 'Autre' }[t] || t);
        const getStatusDot = (s) => {
          switch (s) {
            case 'available':   return { cls: 'occ-available',   label: 'Disponible' };
            case 'occupied':    return { cls: 'occ-occupied',    label: 'Occupé' };
            case 'maintenance': return { cls: 'occ-maintenance', label: 'Maintenance' };
            case 'reserved':    return { cls: 'occ-reserved',    label: 'Réservé' };
            default:            return { cls: '',                 label: 'Indisponible' };
          }
        };

        return (
          <div className="card">
            <div className="card-header">
              <h2>Unités &amp; Appartements ({units.length})</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Toggle vue */}
                <div className="view-toggle">
                  <button
                    className={`view-toggle-btn ${unitsView === 'grid' ? 'active' : ''}`}
                    onClick={() => setUnitsView('grid')} title="Vue cartes"
                  ><FiGrid size={15} /></button>
                  <button
                    className={`view-toggle-btn ${unitsView === 'table' ? 'active' : ''}`}
                    onClick={() => setUnitsView('table')} title="Vue tableau"
                  ><FiList size={15} /></button>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => handleOpenUnitModal()}>
                  <FiPlus /> Ajouter une unité
                </button>
              </div>
            </div>
            <div className="card-body">
              {/* Barre d'occupation */}
              {units.length > 0 && (
                <div className="occupancy-bar">
                  {nbAvailable > 0 && (
                    <span className="occ-stat occ-available">
                      <span className="occ-dot" />{nbAvailable} Disponible{nbAvailable > 1 ? 's' : ''}
                    </span>
                  )}
                  {nbOccupied > 0 && (
                    <span className="occ-stat occ-occupied">
                      <span className="occ-dot" />{nbOccupied} Occupé{nbOccupied > 1 ? 's' : ''}
                    </span>
                  )}
                  {nbMaintenance > 0 && (
                    <span className="occ-stat occ-maintenance">
                      <span className="occ-dot" />{nbMaintenance} Maintenance
                    </span>
                  )}
                  {nbReserved > 0 && (
                    <span className="occ-stat occ-reserved">
                      <span className="occ-dot" />{nbReserved} Réservé{nbReserved > 1 ? 's' : ''}
                    </span>
                  )}
                  <div className="occ-progress" title={`Taux d'occupation : ${occupancyRate}%`}>
                    <div className="occ-progress-fill" style={{ width: `${occupancyRate}%` }} />
                  </div>
                  <span className="occ-label">Taux d'occupation : {occupancyRate}%</span>
                </div>
              )}

              {units.length > 0 ? (
                unitsView === 'grid' ? (
                  /* ── Vue GRILLE ── */
                  <div className="units-grid">
                    {units.map(unit => {
                      const st = unit.computed_status || unit.status || 'unavailable';
                      const sd = getStatusDot(st);
                      return (
                        <div key={unit.id} className={`unit-card unit-${st}`}>
                          <div className="unit-card-status-bar" />
                          <div className="unit-card-body">
                            <div className="unit-card-header">
                              <h4>{unitTypeLabel(unit.unit_type)} {unit.unit_number}</h4>
                              <span className={`occ-stat ${sd.cls}`} style={{ padding: '3px 8px', fontSize: 11 }}>
                                <span className="occ-dot" />{sd.label}
                              </span>
                            </div>

                            <div className="unit-card-meta">
                              {unit.floor !== null && unit.floor !== undefined ? `Étage ${unit.floor}` : 'RDC'}
                              {unit.surface_area && <> · <strong>{unit.surface_area} m²</strong></>}
                              {unit.num_rooms && <> · {unit.num_rooms} p.</>}
                            </div>

                            <div className="unit-card-rent">
                              {parseInt(unit.monthly_rent || 0).toLocaleString()}
                              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4 }}>
                                {unit.currency || 'XOF'}/mois
                              </span>
                            </div>
                            {(unit.agency_fee > 0 || unit.charges > 0) && (
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                                + {parseInt(unit.agency_fee || unit.charges).toLocaleString()} XOF frais d'agence
                              </div>
                            )}

                            {unit.tenant_first_name && (
                              <div className="unit-card-tenant">
                                <FiUser size={13} />
                                <span>{unit.tenant_first_name} {unit.tenant_last_name}</span>
                              </div>
                            )}

                            <div className="unit-card-actions">
                              {st === 'available' && (
                                <Link
                                  to={`/leases?property=${id}&unit=${unit.id}`}
                                  className="btn btn-primary btn-sm"
                                >
                                  Créer un bail
                                </Link>
                              )}
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleOpenUnitModal(unit)}
                                title="Éditer"
                              >
                                <FiEdit size={13} />
                              </button>
                              <Link
                                to={`/properties/${id}/units/${unit.id}`}
                                className="btn btn-secondary btn-sm"
                                title="Détails"
                              >
                                Détails
                              </Link>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteUnit(unit.id)}
                                title="Supprimer"
                                disabled={st === 'occupied'}
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* ── Vue TABLEAU ── */
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>N° Unité</th>
                          <th>Étage</th>
                          <th>Type</th>
                          <th>Surface</th>
                          <th>Pièces</th>
                          <th>Loyer mensuel</th>
                          <th>Statut</th>
                          <th>Locataire</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {units.map(unit => {
                          const st = unit.computed_status || unit.status || 'unavailable';
                          return (
                            <tr key={unit.id} className={`units-table-row-${st}`}>
                              <td><strong>{unit.unit_number}</strong></td>
                              <td>{unit.floor !== null && unit.floor !== undefined ? `Étage ${unit.floor}` : 'RDC'}</td>
                              <td>{unitTypeLabel(unit.unit_type)}</td>
                              <td>{unit.surface_area ? `${unit.surface_area} m²` : '-'}</td>
                              <td>{unit.num_rooms ? `${unit.num_rooms} p.` : '-'}</td>
                              <td><strong>{parseInt(unit.monthly_rent || 0).toLocaleString()} {unit.currency || 'XOF'}</strong></td>
                              <td>{getUnitStatusBadge(st)}</td>
                              <td>
                                {unit.tenant_first_name
                                  ? <span><FiUser size={12} style={{ marginRight: 4 }} />{unit.tenant_first_name} {unit.tenant_last_name}</span>
                                  : <span style={{ color: 'var(--text-secondary)' }}>—</span>
                                }
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: 5 }}>
                                  {st === 'available' && (
                                    <Link to={`/leases?property=${id}&unit=${unit.id}`} className="btn btn-success btn-sm" title="Créer un bail">
                                      <FiPlus size={12} />
                                    </Link>
                                  )}
                                  <button className="btn btn-secondary btn-sm" onClick={() => handleOpenUnitModal(unit)} title="Éditer">
                                    <FiEdit size={13} />
                                  </button>
                                  <Link to={`/properties/${id}/units/${unit.id}`} className="btn btn-secondary btn-sm" title="Détails">→</Link>
                                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUnit(unit.id)} title="Supprimer" disabled={st === 'occupied'}>
                                    <FiTrash2 size={13} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <div className="empty-state">
                  <FiHome size={32} style={{ opacity: 0.4 }} />
                  <p style={{ marginTop: 12 }}>Aucune unité. Cliquez sur "Ajouter une unité" pour créer un appartement, studio ou bureau.</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Modal Ajout / Édition d'unité */}
      {showUnitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-white, #ffffff)', borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3>{editingUnit ? `Éditer l'unité ${editingUnit.unit_number}` : 'Ajouter une nouvelle unité / appartement'}</h3>
              <button onClick={() => setShowUnitModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX /></button>
            </div>
            <form onSubmit={handleSaveUnit}>
              <div className="grid-2">
                <div className="form-group">
                  <label>Numéro / Identifiant d'unité *</label>
                  <input className="form-control" placeholder="ex: A101, Appart 3, Bureau 12" value={unitForm.unit_number} onChange={e => setUnitForm({ ...unitForm, unit_number: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Étage</label>
                  <input type="number" className="form-control" placeholder="ex: 1" value={unitForm.floor} onChange={e => setUnitForm({ ...unitForm, floor: e.target.value })} />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label>Type d'unité</label>
                  <select className="form-control" value={unitForm.unit_type} onChange={e => setUnitForm({ ...unitForm, unit_type: e.target.value })}>
                    <option value="apartment">Appartement</option>
                    <option value="studio">Studio</option>
                    <option value="office">Bureau</option>
                    <option value="shop">Magasin / Commerce</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select className="form-control" value={unitForm.status} onChange={e => setUnitForm({ ...unitForm, status: e.target.value })}>
                    <option value="available">Disponible</option>
                    <option value="occupied">Occupé</option>
                    <option value="reserved">Réservé</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="unavailable">Indisponible</option>
                  </select>
                </div>
              </div>

              <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Surface (m²)</label>
                  <input type="number" step="0.01" className="form-control" value={unitForm.surface_area} onChange={e => setUnitForm({ ...unitForm, surface_area: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nb pièces</label>
                  <input type="number" className="form-control" value={unitForm.num_rooms} onChange={e => setUnitForm({ ...unitForm, num_rooms: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Nb chambres</label>
                  <input type="number" className="form-control" value={unitForm.num_bedrooms} onChange={e => setUnitForm({ ...unitForm, num_bedrooms: e.target.value })} />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label>Loyer mensuel (XOF) *</label>
                  <input type="number" className="form-control" placeholder="ex: 150000" value={unitForm.monthly_rent} onChange={e => setUnitForm({ ...unitForm, monthly_rent: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Frais d'agence (XOF)</label>
                  <input type="number" className="form-control" placeholder="ex: 50000" value={unitForm.agency_fee} onChange={e => setUnitForm({ ...unitForm, agency_fee: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Montant du dépôt de garantie (XOF)</label>
                <input type="number" className="form-control" placeholder="ex: 300000" value={unitForm.deposit_amount} onChange={e => setUnitForm({ ...unitForm, deposit_amount: e.target.value })} />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={unitForm.is_furnished} onChange={e => setUnitForm({ ...unitForm, is_furnished: e.target.checked })} />
                  Meublé
                </label>
              </div>

              <div className="form-group">
                <label>Description / Notes</label>
                <textarea className="form-control" rows="3" placeholder="Informations complémentaires sur l'unité..." value={unitForm.description} onChange={e => setUnitForm({ ...unitForm, description: e.target.value })} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowUnitModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingUnit ? 'Enregistrer les modifications' : 'Créer l\'unité'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ajout propriétaire */}
      {showOwnerModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-white, #ffffff)', borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3>Ajouter un propriétaire</h3>
              <button onClick={() => setShowOwnerModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX /></button>
            </div>
            <form onSubmit={handleAddOwner}>
              <div className="grid-2">
                <div className="form-group">
                  <label>Prénom *</label>
                  <input className="form-control" value={ownerForm.first_name} onChange={e => setOwnerForm({ ...ownerForm, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input className="form-control" value={ownerForm.last_name} onChange={e => setOwnerForm({ ...ownerForm, last_name: e.target.value })} required />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" className="form-control" value={ownerForm.email} onChange={e => setOwnerForm({ ...ownerForm, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input className="form-control" value={ownerForm.phone} onChange={e => setOwnerForm({ ...ownerForm, phone: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Part de propriété (%)</label>
                <input type="number" min="0" max="100" className="form-control" value={ownerForm.ownership_percentage} onChange={e => setOwnerForm({ ...ownerForm, ownership_percentage: e.target.value })} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowOwnerModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ajout URL vidéo */}
      {showVideoUrlModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-white, #ffffff)', borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3>Ajouter une vidéo par URL</h3>
              <button onClick={() => setShowVideoUrlModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX /></button>
            </div>
            <form onSubmit={handleAddVideoUrl}>
              <div className="form-group">
                <label>URL de la vidéo * (YouTube, Vimeo, ou lien direct)</label>
                <input className="form-control" placeholder="https://www.youtube.com/watch?v=..." value={videoUrlForm.url} onChange={e => setVideoUrlForm({ ...videoUrlForm, url: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Titre</label>
                <input className="form-control" placeholder="Titre de la vidéo" value={videoUrlForm.title} onChange={e => setVideoUrlForm({ ...videoUrlForm, title: e.target.value })} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowVideoUrlModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Ajouter</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailPage;